/* eslint-disable react/no-unused-prop-types */
import React from 'react';
import { Badge, Crate, FlexCrate, Text } from '@perch-labs/shrike';
import { copyFor } from 'config';
import { formatAsMoney, formatDate } from '@perch-labs/warbler';
import { ToUtilityCrate, TotalDueArcadia } from './dual-billing-overview.style';

const getCopy = copyFor('billing.statement.dualBillingOverview');

interface PaymentSplitProps {
  collectAutomatically: boolean;
  utilityDisplayName: string;
  paymentDate: string;
  paysByCheck: boolean;
  total: number;
  totalDualBillingUtilityCreditCents: number;
}

const PaymentSplit: React.FC<PaymentSplitProps> = props => {
  return (
    <FlexCrate width="100%" flexWrap="wrap">
      <ToUtilityCrate
        border={{
          default: {
            bottom: { width: '1px', style: 'solid', color: 'primaryInverse' },
          },
          tabletPortraitUp: {
            right: { width: '1px', style: 'solid', color: 'primaryInverse' },
            bottom: { style: 'none' },
          },
        }}
      >
        <ToArcadiaContent {...props} />
      </ToUtilityCrate>
      <ToUtilityCrate>
        <Crate padding={{ default: '0', tabletPortraitUp: { left: '24px' } }}>
          <ToUtilityContent {...props} />
        </Crate>
      </ToUtilityCrate>
    </FlexCrate>
  );
};

const ToArcadiaContent: React.FC<PaymentSplitProps> = ({
  collectAutomatically,
  paymentDate,
  paysByCheck,
  total,
}) => {
  const formattedPaymentDate = formatDate(paymentDate);
  return (
    <>
      <Text textStyle="heading600" color="secondary" margin={{ bottom: '8px' }}>
        {getCopy('toUtility', { utilityDisplayName: 'Arcadia' })}
      </Text>
      <TotalDueArcadia>
        {formatAsMoney(total, { conversion: 'fromCents' })}
      </TotalDueArcadia>
      {collectAutomatically ? (
        <>
          <Badge backgroundColor="success" margin={{ bottom: '8px' }}>
            {getCopy('noActionNeeded')}
          </Badge>
          <Text textStyle="paragraph400" color="secondary">
            {getCopy('autoCharged', { formattedPaymentDate })}
          </Text>
        </>
      ) : (
        <>
          <Badge backgroundColor="warning" margin={{ bottom: '8px' }}>
            {paysByCheck ? getCopy('payByCheck') : getCopy('actionNeeded')}
          </Badge>
          <Text textStyle="paragraph400" color="secondary">
            {getCopy('dueOn', { formattedPaymentDate })}
          </Text>
        </>
      )}
    </>
  );
};

const ToUtilityContent: React.FC<PaymentSplitProps> = ({
  utilityDisplayName,
  totalDualBillingUtilityCreditCents,
}) => {
  const formattedArcadiaPortion = formatAsMoney(
    totalDualBillingUtilityCreditCents,
    { conversion: 'fromCents' }
  );
  return (
    <>
      <Text textStyle="heading600" color="secondary" margin={{ bottom: '8px' }}>
        {getCopy('toUtility', { utilityDisplayName })}
      </Text>
      <Badge backgroundColor="warning" margin={{ bottom: '8px' }}>
        {getCopy('possibleActionNeeded')}
      </Badge>
      <Text textStyle="paragraph400">
        {getCopy('payRemainder', {
          formattedArcadiaPortion,
          utilityDisplayName,
        })}
      </Text>
    </>
  );
};

export type { PaymentSplitProps };
export { PaymentSplit };
